import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  googleAuthenticate,
  googleRegister,
  login,
  register,
  phoneLogin,
  phoneRegister as phoneRegisterApi,
  phoneLoginOtp,
} from "../services/authService";
import { useAuthStore } from "../store/useAuthStore";
import { User, UserRole } from "../types/user";
import { formatApiError } from "../utils/formatApiError";
import { trackCompleteRegistration } from "../utils/metaPixel";
// Fix #4: Use the shared, robust decoder instead of a private duplicate
import { decodeJwtToken } from "../utils/jwtUtils";

// Query keys for better cache management
export const authKeys = {
  all: ["auth"] as const,
  user: () => [...authKeys.all, "user"] as const,
};

/**
 * Build a User object from a decoded JWT token.
 * Replaces the old createUserFromPayload + parseJWT combo.
 */
const buildUserFromToken = (
  token: string,
  fallbackAuthType: "manual" | "gmail" = "manual",
  profilePicture?: string
): User | null => {
  const decoded = decodeJwtToken(token);
  if (!decoded) return null;

  const authType =
    (decoded.authType as "manual" | "gmail" | "google") || fallbackAuthType;

  const hasSetPassword =
    decoded.hasSetPassword !== undefined
      ? decoded.hasSetPassword
      : authType !== "gmail" && authType !== "google";

  return {
    id: decoded.id,
    email: decoded.email,
    firstName: decoded.firstName,
    lastName: decoded.lastName,
    role: (decoded.roles[0] as UserRole) || UserRole.Customer,
    isVerified: decoded.isVerified,
    authType,
    hasSetPassword,
    profilePicture: profilePicture,
    preferences: {
      language: "en",
      currency: "USD",
      notifications: { email: true, sms: false, push: true },
      theme: "light",
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

export const useAuthActions = () => {
  const { setToken, setUser, setRefreshToken } = useAuthStore();
  const queryClient = useQueryClient();

  // Email/Password Login
  const loginMutation = useMutation({
    mutationFn: async (data: { email: string; password: string }) => {
      const response = await login(data);
      if (!response.isSuccess) throw new Error(response.message || "GENERIC_ERROR");
      if (!response.data?.token) throw new Error("NO_AUTH_TOKEN");
      return response;
    },
    mutationKey: authKeys.user(),
    onSuccess: (response) => {
      const token = response.data!.token!;
      setToken(token);
      if (response.data?.refreshToken) setRefreshToken(response.data.refreshToken);
      const user = buildUserFromToken(token, "manual");
      if (user) {
        setUser(user);
      } else {
        console.warn("Login successful but failed to parse user data from token");
      }
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
    onError: (error) => {
      console.error("Login error:", error);
    },
  });

  // Google Authentication
  const googleAuthMutation = useMutation({
    mutationFn: async (data: { idToken: string }) => {
      const response = await googleAuthenticate(data);
      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || "GENERIC_ERROR");
      }
      const { data: authData } = response;
      if (authData.requiresEmailVerification) throw new Error("REQUIRES_EMAIL_VERIFICATION");
      if (authData.requiresEmailLogin) throw new Error("REQUIRES_EMAIL_LOGIN");
      if (!authData.isNewUser && !authData.token) throw new Error("NO_AUTH_TOKEN");
      return response;
    },
    mutationKey: authKeys.user(),
    onSuccess: (response) => {
      const data = response.data!;
      if (data.isNewUser) return data;

      const token = data.token!;
      setToken(token);
      if (data.refreshToken) setRefreshToken(data.refreshToken);
      const user = buildUserFromToken(token, "gmail", data.profilePicture);
      if (user) {
        setUser(user);
      } else {
        // Fallback to response data if JWT parsing fails
        const fallbackUser: User = {
          id: data.userId,
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          role: UserRole.Customer,
          isVerified: data.isVerified,
          authType: "gmail",
          profilePicture: data.profilePicture,
          preferences: {
            language: "en",
            currency: "USD",
            notifications: { email: true, sms: false, push: true },
            theme: "light",
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setUser(fallbackUser);
      }
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
      return data;
    },
    onError: (error) => {
      console.error("Google authentication error:", error);
    },
  });

  // Manual Registration
  const registerMutation = useMutation({
    mutationFn: async (data: Parameters<typeof register>[0]) => {
      const response = await register(data);
      if (!response.isSuccess) throw new Error(response.message || "GENERIC_ERROR");
      return response;
    },
    mutationKey: authKeys.user(),
    onSuccess: (response) => {
      trackCompleteRegistration("manual");
      return response.data;
    },
    onError: (error) => {
      console.error("Registration error:", error);
    },
  });

  // Google Registration
  const googleRegisterMutation = useMutation({
    mutationFn: async (data: Parameters<typeof googleRegister>[0]) => {
      const response = await googleRegister(data);
      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || "GENERIC_ERROR");
      }
      if (!response.data.token) throw new Error("NO_AUTH_TOKEN");
      return response;
    },
    mutationKey: authKeys.user(),
    onSuccess: (response) => {
      const { data } = response;
      const token = data!.token!;
      setToken(token);
      if (data!.refreshToken) setRefreshToken(data!.refreshToken);
      const user = buildUserFromToken(token, "gmail", data!.profilePicture);
      if (user) {
        setUser(user);
      } else {
        // Fallback to response data if JWT parsing fails
        const fallbackUser: User = {
          id: data!.userId,
          email: data!.email,
          firstName: data!.firstName,
          lastName: data!.lastName,
          isVerified: data!.isVerified,
          profilePicture: data!.profilePicture,
          role: UserRole.Customer,
          authType: "gmail",
          preferences: {
            language: "en",
            currency: "USD",
            notifications: { email: true, sms: false, push: true },
            theme: "light",
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setUser(fallbackUser);
      }
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
      trackCompleteRegistration("google");
      return data;
    },
    onError: (error) => {
      console.error("Google registration error:", error);
    },
  });

  // Phone + Password Login
  const phoneLoginMutation = useMutation({
    mutationFn: async (data: { phoneNumber: string; password: string }) => {
      const response = await phoneLogin(data);
      if (!response.isSuccess) throw new Error(response.message || "GENERIC_ERROR");
      if (!response.data?.token) throw new Error("NO_AUTH_TOKEN");
      return response;
    },
    mutationKey: [...authKeys.user(), "phone-login"],
    onSuccess: (response) => {
      const token = response.data!.token!;
      setToken(token);
      if (response.data?.refreshToken) setRefreshToken(response.data.refreshToken);
      const user = buildUserFromToken(token, "manual");
      if (user) setUser(user);
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
  });

  // Phone Registration (after OTP verified)
  const phoneRegisterMutation = useMutation({
    mutationFn: async (data: {
      phoneNumber: string; countryCode: string;
      email: string; firstName: string; lastName: string;
      password: string; confirmPassword: string;
      referralCode?: string;
    }) => {
      const response = await phoneRegisterApi(data);
      if (!response.isSuccess) throw new Error(response.message || "GENERIC_ERROR");
      if (!response.data?.token) throw new Error("NO_AUTH_TOKEN");
      return response;
    },
    mutationKey: [...authKeys.user(), "phone-register"],
    onSuccess: (response) => {
      const token = response.data!.token!;
      setToken(token);
      if (response.data?.refreshToken) setRefreshToken(response.data.refreshToken);
      const user = buildUserFromToken(token, "manual");
      if (user) setUser(user);
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
      trackCompleteRegistration("phone");
    },
  });

  // Phone OTP Login
  const phoneOtpLoginMutation = useMutation({
    mutationFn: async (data: { phoneNumber: string; otp: string }) => {
      const response = await phoneLoginOtp(data);
      if (!response.isSuccess) throw new Error(response.message || "GENERIC_ERROR");
      if (!response.data?.token) throw new Error("NO_AUTH_TOKEN");
      return response;
    },
    mutationKey: [...authKeys.user(), "phone-otp-login"],
    onSuccess: (response) => {
      const token = response.data!.token!;
      setToken(token);
      if (response.data?.refreshToken) setRefreshToken(response.data.refreshToken);
      const user = buildUserFromToken(token, "manual");
      if (user) setUser(user);
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
  });

  /**
   * Get formatted error message from any mutation
   */
  const getErrorMessage = (): string | null => {
    const error =
      loginMutation.error ||
      googleAuthMutation.error ||
      registerMutation.error ||
      googleRegisterMutation.error ||
      phoneLoginMutation.error ||
      phoneRegisterMutation.error ||
      phoneOtpLoginMutation.error;

    if (!error) return null;

    return formatApiError(error);
  };

  /**
   * ✅ FIX: Aggregate authentication state from all mutations
   * Memoized to prevent unnecessary re-renders
   */
  const authState = useMemo(() => {
    const mutations = [
      loginMutation,
      googleAuthMutation,
      registerMutation,
      googleRegisterMutation,
      phoneLoginMutation,
      phoneRegisterMutation,
      phoneOtpLoginMutation,
    ];

    return {
      isLoading: mutations.some((m) => m.isPending),
      isSuccess: mutations.some((m) => m.isSuccess),
      error: mutations.find((m) => m.error)?.error || null,
    };
  }, [
    loginMutation,
    googleAuthMutation,
    registerMutation,
    googleRegisterMutation,
    phoneLoginMutation,
    phoneRegisterMutation,
    phoneOtpLoginMutation,
  ]);

  return {
    // Mutations
    login: loginMutation,
    googleAuth: googleAuthMutation,
    register: registerMutation,
    googleRegister: googleRegisterMutation,
    phoneLogin: phoneLoginMutation,
    phoneRegister: phoneRegisterMutation,
    phoneOtpLogin: phoneOtpLoginMutation,

    // Aggregated state
    isLoading: authState.isLoading,
    isSuccess: authState.isSuccess,
    error: authState.error,
    errorMessage: getErrorMessage(),
  };
};
